import React, { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Button, Col, Layout, Row } from 'antd';
import { SearchInput } from 'common/components/Input/SearchInput';
import { PageHeader } from 'common/components/Page/PageHeader';
import { PageContent } from 'common/components/Page/PageContent';
import { DataTable } from 'common/components/Table/DataTable';
import { Selector } from 'common/components/Selector/Selector';
import { Loader } from 'common/components/Loader';
import { Calendar, Funnel } from 'common/svg';
import { PeriodFilterCard } from 'common/components/PeriodFilterCard';
import { PageStoreSelector } from 'common/components/PageStoreSelector';
import { getWeekOptions } from 'common/helpers/date.helper';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  getPurchaseOrderCollection,
  getPurchaseOrderStoreCollection,
  setPurchaseOrderCollection,
} from 'app/store/reducers/purchaseOrder.reducer';
import {
  EPurchaseOrderStatus,
  deliveryHistoryConfig,
  IPurchaseOrder,
  VIEW_DELIVERY_HISTORY_SORT_FIELD,
  IPurchaseOrderCollectionParams,
  VIEW_DELIVERY_HISTORY_LIMIT,
} from 'entities/PurchaseOrder/PurchaseOrder.models';
import { purchaseOrderTransport } from 'entities/PurchaseOrder/PurchaseOrder.transport';
import { getStorageStoreCode } from 'entities/Store/Store.helper';
import { ALL_STORES_VALUE } from 'entities/Store/components/Selector/StoreSelectorCir';

export const ViewDeliveryHistoryPage: FC = () => {
  const dispatch = useAppDispatch();
  const { purchaseOrderCollection, purchaseOrderCollectionLoading, purchaseOrderStoreCollection } = useAppSelector(
    (state) => state.purchaseOrder
  );

  const [openPeriodFilterCard, setOpenPeriodFilterCard] = useState<boolean>(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState<boolean>(false);
  const [ordersParams, setOrdersParams] = useState<IPurchaseOrderCollectionParams>(() => {
    const storeCode = getStorageStoreCode();

    return {
      statuses: [EPurchaseOrderStatus.Delivered, EPurchaseOrderStatus.Canceled],
      limit: VIEW_DELIVERY_HISTORY_LIMIT,
      offset: 0,
      storeCode: storeCode && storeCode !== ALL_STORES_VALUE ? storeCode : undefined,
    };
  });
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    dispatch(getPurchaseOrderStoreCollection());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPurchaseOrderCollection(ordersParams));
  }, [ordersParams, dispatch]);

  const weekOptions = useMemo(() => getWeekOptions(ordersParams.year), [ordersParams.year]);
  const orders = purchaseOrderCollection?.data || [];
  const loading = purchaseOrderCollectionLoading || loadMoreLoading;

  // MBC-81: coverage = диапазон выбранной недели (или текущей iso-недели если фильтр не задан).
  // ISO-неделя (Mon–Sun) — соответствует BE-окну в purchaseOrder.service.ts::list (MBC-51-fix).
  const coverageRange = (() => {
    const base = ordersParams.week
      ? moment()
          .isoWeekYear(ordersParams.year ?? moment().year())
          .isoWeek(ordersParams.week)
      : moment();
    return {
      start: base.clone().startOf('isoWeek'),
      end: base.clone().endOf('isoWeek'),
    };
  })();
  const coverage = `${coverageRange.start.format('MMMM DD, YYYY')} to ${coverageRange.end.format('MMMM DD, YYYY')}`;
  const filterActive = openPeriodFilterCard || ordersParams.storeCode || ordersParams.week;

  const changeSearch = (sonum: string): void => {
    setPage(0);
    setOrdersParams((prev) => ({ ...prev, sonum: sonum.length ? sonum : undefined, offset: 0 }));
  };

  const changeStore = (storeCode?: string | number): void => {
    setPage(0);
    setOrdersParams((prev) => {
      if (prev.storeCode === (storeCode as string | undefined)) {
        return prev;
      }

      return { ...prev, storeCode: storeCode as string | undefined, offset: 0 };
    });
  };

  const changeYear = (year?: number): void => {
    setPage(0);
    setOrdersParams((prev) => ({ ...prev, year, offset: 0 }));
  };

  const changeWeek = (week?: number): void => {
    setPage(0);
    setOrdersParams((prev) => ({ ...prev, week, offset: 0 }));
  };

  const handleChangeWeek = (week?: string | number): void => {
    if (!week) {
      setPage(0);
      setOrdersParams((prev) => ({ ...prev, year: undefined, week: undefined, offset: 0 }));
    } else {
      setPage(0);
      setOrdersParams((prev) => ({ ...prev, week: week as number, offset: 0 }));
    }
  };

  const sortData = (field: string, order?: string): void => {
    // MBC-79: map antd dataIndex → BE enum value; antd 'ascend'/'descend' → 'asc'/'desc'.
    // Unknown field (e.g., receivedBy after sorter:false) or cleared sort (order=undefined) → reset.
    const beField = VIEW_DELIVERY_HISTORY_SORT_FIELD[field];
    setPage(0);

    if (!beField || !order) {
      setOrdersParams((prev) => ({ ...prev, orderField: undefined, orderDirection: undefined, offset: 0 }));
      return;
    }

    setOrdersParams((prev) => ({
      ...prev,
      orderField: beField as any,
      orderDirection: order === 'ascend' ? 'ASC' : 'DESC',
      offset: 0,
    }));
  };

  const openFilterCard = (): void => {
    setOpenPeriodFilterCard(true);
  };

  const closeFilterCard = (): void => {
    setOpenPeriodFilterCard(false);
  };

  const loadMoreOrders = async (): Promise<void> => {
    if (!purchaseOrderCollection) {
      return;
    }

    setLoadMoreLoading(true);

    try {
      const orders = await purchaseOrderTransport.getCollection({
        ...ordersParams,
        offset: VIEW_DELIVERY_HISTORY_LIMIT * (page + 1),
      });

      setPage((prev) => prev + 1);
      dispatch(
        setPurchaseOrderCollection({
          ...purchaseOrderCollection,
          data: [...purchaseOrderCollection.data, ...orders.data],
        })
      );
    } finally {
      setLoadMoreLoading(false);
    }
  };

  return (
    <Layout className="relative height-full flex flex__column">
      {loading && <Loader />}

      <PageHeader
        title="View Delivery History"
        extra={
          <Row gutter={[8, 8]}>
            <Col style={{ width: 300 }}>
              <SearchInput placeholder="Search by SO#" value={ordersParams.sonum ?? ''} onChange={changeSearch} />
            </Col>
            <Col style={{ maxWidth: 149 }}>
              <PageStoreSelector stores={purchaseOrderStoreCollection?.data} onChange={changeStore} allowAll />
            </Col>
            <Col>
              <Selector
                suffix={<Calendar />}
                placeholder={
                  <>
                    <Calendar />
                    <span>Select Period</span>
                  </>
                }
                options={weekOptions}
                value={ordersParams.week}
                onChange={handleChangeWeek}
              />
            </Col>
            <Col>
              <Button
                className={`btn-gray square ${filterActive ? 'btn-gray-selected' : ''}`}
                icon={<Funnel className={filterActive ? 'icon-purple' : 'icon-gray'} />}
                onClick={openFilterCard}
              />
            </Col>
          </Row>
        }
      />

      <PageContent>
        <div className="page-content__info">
          <Row gutter={[{ xs: 16, md: 32 }, 12]}>
            <Col>
              <div className="mb-3 page-content__info-label">Coverage</div>
              <span className="ff-regular fs-xxs">{`${coverage} (12:00 am — 11:59 pm)`}</span>
            </Col>
          </Row>
        </div>

        <div className="page-content__container">
          <DataTable<IPurchaseOrder>
            dataSource={orders}
            config={deliveryHistoryConfig}
            onSort={sortData}
            loadMore={loadMoreOrders}
            loading={loading}
            hasMore={purchaseOrderCollection?.data.length < purchaseOrderCollection?.meta.count}
          />
        </div>

        <PeriodFilterCard
          open={openPeriodFilterCard}
          year={ordersParams.year}
          week={ordersParams.week}
          onYearChange={changeYear}
          onWeekChange={changeWeek}
          onCancel={closeFilterCard}
        />
      </PageContent>
    </Layout>
  );
};
